/**
 * Simple Markdown Renderer
 * 
 * A lightweight markdown-to-JSX converter that doesn't require heavy libraries.
 * Supports common markdown syntax.
 */

import React from 'react';
import PropTypes from 'prop-types';
import CodeBlock from './CodeBlock';

/**
 * Parse inline markdown (bold, italic, inline code, links)
 */
const parseInline = (text) => {
  if (!text) return '';
  
  // Replace inline code: `code`
  text = text.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
  
  // Replace bold: **text** or __text__
  text = text.replace(/\*\*([^*]+)\*\*|__([^_]+)__/g, '<strong>$1$2</strong>');
  
  // Replace italic: *text* or _text_
  text = text.replace(/\*([^*]+)\*|_([^_]+)_/g, '<em>$1$2</em>');
  
  // Replace links: [text](url)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
  
  return text;
};

/**
 * Parse a table row into cells
 */
const parseTableRow = (row) => {
  return row
    .split('|')
    .slice(1, -1) // Remove first and last empty cells from leading/trailing |
    .map(cell => cell.trim());
};

/**
 * Simple markdown parser
 */
const parseMarkdown = (markdown) => {
  if (!markdown) return [];
  
  const lines = markdown.split('\n');
  const elements = [];
  let i = 0;
  let frontmatterSkipped = false;
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Skip frontmatter delimiter lines (only the first --- pair at the start)
    if (line.trim() === '---' && !frontmatterSkipped && i === 0) {
      // Skip until next ---
      i++;
      while (i < lines.length && lines[i].trim() !== '---') {
        i++;
      }
      i++; // Skip the closing ---
      frontmatterSkipped = true;
      continue;
    }
    
    // Skip empty lines at start
    if (elements.length === 0 && !line.trim()) {
      i++;
      continue;
    }
    
    // Headers - Skip the first H1 (title) since it's already shown in page header
    const h1Match = line.match(/^# (.+)$/);
    if (h1Match) {
      // Skip if this is the first content element (main title)
      if (elements.length === 0) {
        i++;
        continue;
      }
      elements.push(
        <h1 
          key={i} 
          className="text-3xl font-bold text-gray-900 mt-10 mb-4 pb-2 border-b border-gray-200"
          dangerouslySetInnerHTML={{ __html: parseInline(h1Match[1]) }}
        />
      );
      i++;
      continue;
    }
    
    const h2Match = line.match(/^## (.+)$/);
    if (h2Match) {
      // First H2 shouldn't have top margin if it's the first element
      const isFirst = elements.length === 0;
      elements.push(
        <h2 
          key={i} 
          className={`text-2xl font-bold text-gray-800 mb-4 ${isFirst ? 'mt-0' : 'mt-8'}`}
          dangerouslySetInnerHTML={{ __html: parseInline(h2Match[1]) }}
        />
      );
      i++;
      continue;
    }
    
    const h3Match = line.match(/^### (.+)$/);
    if (h3Match) {
      elements.push(
        <h3 
          key={i} 
          className="text-xl font-semibold text-gray-800 mt-6 mb-3"
          dangerouslySetInnerHTML={{ __html: parseInline(h3Match[1]) }}
        />
      );
      i++;
      continue;
    }
    
    const h4Match = line.match(/^#### (.+)$/);
    if (h4Match) {
      elements.push(
        <h4 
          key={i} 
          className="text-xl font-semibold text-gray-800 mt-6 mb-2"
          dangerouslySetInnerHTML={{ __html: parseInline(h4Match[1]) }}
        />
      );
      i++;
      continue;
    }
    
    // Horizontal rule
    if (line.match(/^-{3,}$/)) {
      elements.push(<hr key={i} className="my-8 border-gray-200" />);
      i++;
      continue;
    }
    
    // Code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      let code = '';
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        code += lines[i] + '\n';
        i++;
      }
      i++; // Skip closing ```
      
      elements.push(
        <CodeBlock key={i} className={lang ? `language-${lang}` : ''}>
          {code.trim()}
        </CodeBlock>
      );
      continue;
    }
    
    // Blockquote
    if (line.startsWith('> ')) {
      let quote = '';
      while (i < lines.length && lines[i].startsWith('> ')) {
        quote += lines[i].slice(2) + ' ';
        i++;
      }
      elements.push(
        <blockquote 
          key={i} 
          className="border-l-4 border-blue-500 pl-4 py-2 my-6 bg-blue-50 italic text-gray-700 rounded-r"
          dangerouslySetInnerHTML={{ __html: parseInline(quote.trim()) }}
        />
      );
      continue;
    }
    
    // Unordered list
    if (line.match(/^[-*] (.+)$/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*] (.+)$/)) {
        const match = lines[i].match(/^[-*] (.+)$/);
        items.push(
          <li 
            key={i} 
            className="leading-relaxed mb-1"
            dangerouslySetInnerHTML={{ __html: parseInline(match[1]) }}
          />
        );
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc list-inside text-gray-700 mb-4 space-y-1 ml-4">
          {items}
        </ul>
      );
      continue;
    }
    
    // Ordered list
    if (line.match(/^\d+\. (.+)$/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\. (.+)$/)) {
        const match = lines[i].match(/^\d+\. (.+)$/);
        items.push(
          <li 
            key={i} 
            className="leading-relaxed mb-1"
            dangerouslySetInnerHTML={{ __html: parseInline(match[1]) }}
          />
        );
        i++;
      }
      elements.push(
        <ol key={i} className="list-decimal list-inside text-gray-700 mb-4 space-y-1 ml-4">
          {items}
        </ol>
      );
      continue;
    }
    
    // Table
    if (line.startsWith('|')) {
      const tableLines = [];
      // Collect all table lines
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      
      // Need at least 2 lines (header and separator) for a valid table
      if (tableLines.length >= 2) {
        const headerRow = parseTableRow(tableLines[0]);
        // Skip separator line (contains only |, -, and spaces)
        const bodyRows = tableLines.slice(2).map(parseTableRow);
        
        elements.push(
          <div key={i} className="overflow-x-auto my-6">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  {headerRow.map((cell, idx) => (
                    <th 
                      key={idx}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider"
                      dangerouslySetInnerHTML={{ __html: parseInline(cell.trim()) }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bodyRows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td 
                        key={cellIdx}
                        className="px-4 py-3 text-sm text-gray-700"
                        dangerouslySetInnerHTML={{ __html: parseInline(cell.trim()) }}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }
    
    // Paragraph (default)
    if (line.trim()) {
      elements.push(
        <p 
          key={i} 
          className="text-gray-700 leading-relaxed mb-4 text-base"
          dangerouslySetInnerHTML={{ __html: parseInline(line) }}
        />
      );
    }
    
    i++;
  }
  
  return elements;
};

/**
 * Markdown Renderer Component
 */
const MarkdownRenderer = ({ content }) => {
  const elements = parseMarkdown(content);
  
  return (
    <div className="prose prose-lg max-w-none">
      {elements}
    </div>
  );
};

MarkdownRenderer.propTypes = {
  content: PropTypes.string.isRequired,
};

export default MarkdownRenderer;
